import React from 'react';
import styles from './tv-guide.module.scss';

import {
  FocusContext,
  getCurrentFocusKey,
  setFocus,
  useFocusable
} from '@noriginmedia/norigin-spatial-navigation';
import { TVGuideProgramExtended } from '@smart-tv/pages/tv-guide/utils/tv-guide-create-empty-programs';
import { NOW_FOCUS_KEY } from '@smart-tv/pages/tv-guide/utils/tv-guide-helpers';
import { TvGuideChannel } from '@smart-tv/pages/tv-guide/types';
import isInView from '@smart-tv/utils/is-in-view/is-in-view';

import TvGuideDayViewContext from '../utils/tv-guide-day-view-context';
import TvGuideProgram from './tv-guide-program';

export interface ProgramsRowProps {
  channelIndex: number;
}

interface Props {
  channel: TvGuideChannel;
  channelIndex: number;
  channelsLength: number;
}

const TvGuideProgramsRow: React.FC<Props> = (props: Props) => {
  const { channel, channelIndex, channelsLength } = props;

  const {
    scroll,
    activeChannelIndex,
    scrollXRef,
    setActiveCenter,
    setVerticalArrowPress,
    setActiveChannelIndex,
    setActiveTVGuideFocusKey,
    scaledConstants
  } = React.useContext(TvGuideDayViewContext);

  const { ref, focusKey } = useFocusable({
    extraProps: { channelIndex }
  });

  const isNextFocusable = React.useCallback(() => {
    if (channelIndex === activeChannelIndex) {
      return true;
    }
    if (activeChannelIndex === undefined && channelIndex === 0) {
      return true;
    }
    return false;
  }, [activeChannelIndex, channelIndex]);

  React.useEffect(() => {
    isNextFocusable();
  }, [isNextFocusable]);

  const isInViewExtendedList = React.useCallback((): TVGuideProgramExtended[] => {
    // Row bounds in scaled Y coordinates. These must match rendered CSS dimensions,
    // otherwise rows can be considered outside viewport when they are not.
    const top = channelIndex * (scaledConstants.programHeight + scaledConstants.programGap);
    const bottom = top + scaledConstants.programHeight;
    const inViewList = (channel.programs as TVGuideProgramExtended[]).filter(
      (program, index, inViewList) => {
        // Program bounds in scaled X coordinates.
        const left: number = program.startTimeInMinutes * scaledConstants.minuteWidth;
        const right: number = program.endTimeInMinutes * scaledConstants.minuteWidth;

        if (left === 0 && right === 0) {
          return;
        }

        let leftOutOfView = left;
        if (index > 0) {
          // Neighbor-assisted visibility check avoids dropping edge programs when only
          // a tiny sliver is visible due to rounding near viewport boundaries.
          leftOutOfView = inViewList[index - 1].startTimeInMinutes * scaledConstants.minuteWidth;
        }

        let rightOutOfView = right;
        if (index < channel.programs.length - 1) {
          rightOutOfView = inViewList[index + 1].endTimeInMinutes * scaledConstants.minuteWidth;
        }

        return (
          isInView(scroll, { top, right, bottom, left }) ||
          isInView(scroll, { top, right, bottom, left: leftOutOfView }) ||
          isInView(scroll, { top, right: rightOutOfView, bottom, left })
        );
      }
    );

    return inViewList;
  }, [
    channel.programs,
    channelIndex,
    scroll,
    scaledConstants.minuteWidth,
    scaledConstants.programHeight,
    scaledConstants.programGap
  ]);

  const handleOnArrowPress = React.useCallback(
    (direction: string, programIndex: number) => {
      if (direction === 'left' || direction === 'right') {
        if (
          (programIndex === 0 && direction === 'left') ||
          (programIndex === channel.programs.length - 1 && direction === 'right')
        ) {
          return false;
        }

        setVerticalArrowPress(false);

        const nextProgram =
          direction === 'left' && programIndex > 0
            ? channel.programs[programIndex - 1]
            : direction === 'right' && programIndex < channel.programs.length - 1
            ? channel.programs[programIndex + 1]
            : channel.programs[programIndex];

        const left = nextProgram.startTimeInMinutes * scaledConstants.minuteWidth;
        const right = nextProgram.endTimeInMinutes * scaledConstants.minuteWidth;

        if (left <= scroll.left || right > scroll.right) {
          // Keep navigation target visible by scrolling just enough, accounting for
          // scaled left padding (`gridSpacing`) used by the grid layout.
          scrollXRef.current?.scrollTo({
            left:
              direction === 'left' || right - left > scroll.right - scroll.left
                ? left - scaledConstants.gridSpacing
                : scroll.left + (right - scroll.right) + scaledConstants.gridSpacing
          });
        }

        if (right - left > scroll.right - scroll.left) {
          setActiveCenter(left - scaledConstants.gridSpacing + (scroll.right - scroll.left) / 2);
        } else {
          setActiveCenter(left + (right - left) / 2);
        }

        setVerticalArrowPress(true);

        return true;
      } else if (direction === 'up' || direction === 'down') {
        if (direction === 'up' && channelIndex === 0) {
          setActiveTVGuideFocusKey(getCurrentFocusKey());
          setFocus(NOW_FOCUS_KEY);
          return false;
        }
        setVerticalArrowPress(true);

        if (direction === 'up' && channelIndex > 0) {
          setActiveChannelIndex(channelIndex - 1);
        } else if (direction === 'down' && channelIndex < channelsLength - 1) {
          setActiveChannelIndex(channelIndex + 1);
        }

        return false;
      }
      return true;
    },
    [
      channel.programs,
      channelIndex,
      channelsLength,
      scroll.left,
      scroll.right,
      scrollXRef,
      setActiveCenter,
      setActiveChannelIndex,
      setActiveTVGuideFocusKey,
      setVerticalArrowPress,
      scaledConstants.minuteWidth,
      scaledConstants.gridSpacing
    ]
  );

  return (
    <FocusContext.Provider value={focusKey}>
      <div className={styles['tv-guide-programs__row']} ref={ref}>
        {isInViewExtendedList().map((program) => {
          // These CSS custom properties are already scaled, so the SCSS layer can place
          // each absolute-positioned card directly without extra runtime math.
          const programTopOffset =
            channelIndex * (scaledConstants.programHeight + scaledConstants.programGap) + 'px';
          const programLeftOffset = program.startTimeInMinutes * scaledConstants.minuteWidth + 'px';
          const programWidth =
            program.durationInMinutes * scaledConstants.minuteWidth -
            scaledConstants.programWGap +
            'px';

          return (
            <TvGuideProgram
              key={program.eventId}
              program={program}
              style={
                {
                  '--program-top-offset': programTopOffset,
                  '--program-left-offset': programLeftOffset,
                  '--program-width': programWidth
                } as React.CSSProperties
              }
              channelIndex={channelIndex}
              focusable={isNextFocusable()}
              onArrowPress={(direction: string) =>
                handleOnArrowPress(
                  direction,
                  channel.programs.findIndex((p) => p.eventId === program.eventId)
                )
              }
            />
          );
        })}
      </div>
    </FocusContext.Provider>
  );
};

export default React.memo(TvGuideProgramsRow);
